import type { Report } from '../../SearchArea/WeeklyMasterProvider';
import { formatYmd } from '../../../common/commonFunc';

// Weekly period resolver:
// converts start/end date fields to consistent UI display text.

export type WeeklyPeriodResolved = {
  startYmd: string;
  endYmd: string;
  issueYmd: string;
  text: string;
  source: 'record' | 'computed' | 'none';
};

const formatYmdFallback = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}/${m}/${day}`;
};

const parseToLocalDate = (v: any): Date | null => {
  if (v == null) return null;

  if (v instanceof Date) {
    const t = v.getTime();
    if (Number.isNaN(t)) return null;
    return new Date(v.getFullYear(), v.getMonth(), v.getDate());
  }

  if (typeof v === 'number') {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return null;
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }

  const s = String(v).trim();
  if (!s) return null;

  const m = s.match(/^(\d{4})[/-](\d{1,2})[/-](\d{1,2})/);
  if (m) {
    const y = Number(m[1]);
    const mo = Number(m[2]);
    const day = Number(m[3]);
    const d = new Date(y, mo - 1, day);
    if (Number.isNaN(d.getTime())) return null;
    return d;
  }

  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

const formatYmdSafe = (v: any): string => {
  try {
    const s = formatYmd(v);
    if (s) return s;
  } catch {
    // noop
  }
  const d = parseToLocalDate(v);
  return d ? formatYmdFallback(d) : '';
};

export const resolveWeeklyPeriod = (r: Report): WeeklyPeriodResolved => {
  const anyR: any = r as any;

  const pickFirst = (keys: string[]): any => {
    for (const k of keys) {
      const v = anyR?.[k];
      if (v == null) continue;
      if (typeof v === 'string' && v.trim() === '') continue;
      return v;
    }
    return null;
  };

  const startRaw = pickFirst(['start_date', 'startDate', 'START_DATE']);
  const endRaw = pickFirst(['end_date', 'endDate', 'END_DATE']);

  const startYmdRecord = formatYmdSafe(startRaw);
  const endYmdRecord = formatYmdSafe(endRaw);

  if (startYmdRecord || endYmdRecord) {
    const text =
      startYmdRecord && endYmdRecord
        ? `${startYmdRecord}〜${endYmdRecord}`
        : endYmdRecord || startYmdRecord;

    return {
      startYmd: startYmdRecord,
      endYmd: endYmdRecord,
      issueYmd: endYmdRecord || startYmdRecord,
      text,
      source: 'record',
    };
  }

  return {
    startYmd: '',
    endYmd: '',
    issueYmd: '',
    text: '',
    source: 'none',
  };
};
